import React, { useMemo } from 'react';
import { useParams } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/ui/data-table';
import { useDocuments, Document } from '@/hooks/useDocuments';
import { Loading } from '@/components/shared';
import {
  createFileTypeColumn,
  createDateColumn,
  createFileNameColumn,
  createSummaryColumn,
  LinkConfig,
} from '@/components/ui/column-builders';

export interface DocumentListPanelProps {
  title: string;
  documentGuids: string[];
  onDocumentClick?: (documentGuid: string) => void;
}

function createDocumentListColumns(linkConfig?: LinkConfig): ColumnDef<Document, unknown>[] {
  return [
    createFileTypeColumn({ linkConfig }) as ColumnDef<Document, unknown>,
    createDateColumn() as ColumnDef<Document, unknown>,
    createFileNameColumn({ linkConfig }) as ColumnDef<Document, unknown>,
    createSummaryColumn({ linkConfig }) as ColumnDef<Document, unknown>,
  ];
}

export function DocumentListPanel({ title, documentGuids, onDocumentClick }: DocumentListPanelProps) {
  const { slug } = useParams({ from: '/matter/$slug' });
  const { documents, isLoading } = useDocuments(slug);

  const guidSet = useMemo(() => new Set(documentGuids), [documentGuids]);
  const filteredDocuments = useMemo(
    () => documents.filter((doc) => guidSet.has(doc.guid)),
    [documents, guidSet],
  );

  const linkConfig: LinkConfig | undefined = useMemo(() => {
    if (!slug || !onDocumentClick) return undefined;
    return { slug, onDocumentClick };
  }, [slug, onDocumentClick]);

  const columns = useMemo(() => createDocumentListColumns(linkConfig), [linkConfig]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">
        {title} <span className="text-gray-400 font-normal">({filteredDocuments.length})</span>
      </h3>
      {filteredDocuments.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">No documents found.</p>
      ) : (
        <div className="relative flex-1 min-h-0">
          <div className="h-full overflow-y-auto">
            <DataTable
              columns={columns}
              data={filteredDocuments}
              hideSearch
              uploadButton={false}
              exportButton={false}
              initialPageSize={20}
            />
          </div>
        </div>
      )}
    </div>
  );
}
